import { useRef, useState, Fragment } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import Box from '@material-ui/core/Box';

import {
  selectNotificationById,
  selectNotifications,
  selectRecentsNotifications
} from '~/store/modules/notifications/notifications-selectors';
import { NotificationInterface } from '@e3labs/isa-data-dictionary';
import ModalNotification from './components/ModalNotification';
import useAppSelector from '~/hooks/useAppSelector';
import { Bell as IconBell } from 'react-feather';
import { formatMessage } from '~/i18n/translate';
import { useHistory } from 'react-router-dom';
import useToggle from '~/hooks/useToggle';

import * as S from './styles';

const NotificationsDropdown = () => {
  const ref = useRef(null);
  const [isOpenPopover, togglePopover] = useToggle(false);
  const [isOpenModal, toggleModal] = useToggle(false);
  const [notificationId, setNotificationId] = useState('');
  const history = useHistory();

  const { loadingData } = useAppSelector(selectNotifications);
  const notifications = useAppSelector(selectRecentsNotifications);
  const total = notifications?.length ?? 0;

  const notificationSelected: NotificationInterface<any> = useAppSelector(selectNotificationById(notificationId));

  const getNotificationInfo = ({ type }: NotificationInterface<any>) => {
    const { orderNumber, clientExternalCode, client, clientName } = type?.payload?.data ?? {};

    return {
      orderNumber,
      clientExternalCode,
      clientName: client?.name ?? clientName
    };
  };

  const handleNotificationView = (id: string) => {
    toggleModal();
    setNotificationId(id);
  };

  const handleNavigateToShowAll = () => {
    togglePopover();
    history.push('/notifications');
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" ref={ref} onClick={togglePopover} disabled={loadingData}>
          <S.Indicator badgeContent={total}>
            {loadingData ? <CircularProgress size={20} color="inherit" /> : <IconBell />}
          </S.Indicator>
        </IconButton>
      </Tooltip>

      <S.Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        anchorEl={ref.current}
        onClose={togglePopover}
        open={isOpenPopover}
      >
        <S.NotificationHeader p={2}>
          <Typography variant="subtitle1" color="textPrimary">
            {`${total} ${formatMessage('notificationNewNotifications')}`}
          </Typography>
        </S.NotificationHeader>

        <List>
          {notifications.map((notification: NotificationInterface<any>, index: number) => {
            const { orderNumber, clientExternalCode, clientName } = getNotificationInfo(notification);

            return (
              <Fragment key={notification?._id}>
                <ListItem button onClick={() => handleNotificationView(notification?._id!)}>
                  <ListItemText
                    primary={notification?.title}
                    secondary={
                      <>
                        {orderNumber && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {formatMessage('notificationLabelOrder')}: {orderNumber}
                          </Typography>
                        )}

                        {(clientExternalCode || clientName) && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {formatMessage('notificationLabelClient')}:{' '}
                            {clientExternalCode ? `${clientExternalCode} - ` : ''}
                            {clientName}
                          </Typography>
                        )}
                        {notification?.shortMessage && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {notification.shortMessage}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
                {index + 1 !== total && <Divider component="li" />}
              </Fragment>
            );
          })}
        </List>

        <Box p={1} display="flex" justifyContent="center">
          <Button size="small" onClick={handleNavigateToShowAll} disabled={loadingData}>
            {loadingData ? <CircularProgress size={20} /> : formatMessage('notificationShowAll')}
          </Button>
        </Box>
      </S.Popover>

      <ModalNotification isOpen={isOpenModal} onClose={toggleModal} notification={notificationSelected} />
    </>
  );
};

export default NotificationsDropdown;
